import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, ArrowUpRight } from "lucide-react";
import Reveal, { MaskedLine } from "@/components/Reveal";

const faqs = [
  {
    q: "Do I need experience with cold water to join a dip?",
    a: "Not at all. Our Sunday community dips at St Bees are designed for beginners. A trained instructor and a welcoming community support you the whole way, with calming exercises before we get in.",
  },
  {
    q: "What should I bring to a community dip?",
    a: "Swimwear, a towel and warm layers for afterwards. We provide the tea, coffee and biscuits. Check our social media each week for confirmed timings.",
  },
  {
    q: "Where do experiences take place?",
    a: "Across the Lake District. Community dips run at St Bees Beach every Sunday, while coaching walks and programmes use locations like Ennerdale, chosen because location has energy.",
  },
  {
    q: "Do I need to be fit for a coaching walk or adventure day?",
    a: "No. Walks move at conversational pace and every activity is tailored to the group. Bespoke options are always available if you have specific needs.",
  },
  {
    q: "What is the Wim Hof Method?",
    a: "A combination of breathing techniques, gradual cold exposure and commitment, proven to build resilience and focus. Dave is a certified Wim Hof Method Instructor and teaches it safely.",
  },
  {
    q: "How do corporate packages work?",
    a: "Every corporate programme is tailored. We start with a chat about your team's physical and mental wellbeing needs, then build a package of talks, workshops and outdoor experiences to suit any group size.",
  },
  {
    q: "What happens after I apply or enquire?",
    a: "Dave personally reviews every application and enquiry, then replies within a couple of days to talk through the right starting point for you.",
  },
];

const Faq = () => {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <main data-testid="faq-page" className="pt-32">
      <section className="section-shell pt-8 md:pt-12" data-testid="faq-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="overline-label mb-5">Questions, answered</p>
              <h1 className="font-display text-5xl sm:text-6xl font-bold tracking-tight text-bone leading-[1.05]" data-testid="faq-headline">
                <MaskedLine delay={0.1}>Before you</MaskedLine>
                <MaskedLine delay={0.25}>take the <span className="text-brand">plunge.</span></MaskedLine>
              </h1>
              <p className="mt-8 text-base sm:text-lg text-slate-400 leading-relaxed max-w-md">
                Everything people usually ask before their first dip, walk or workshop. Something missing? Just ask.
              </p>
              <Link to="/contact" data-testid="faq-contact-btn" className="btn-secondary mt-10">
                Ask a question <ArrowUpRight size={14} />
              </Link>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            {faqs.map((f, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className="border-b border-white/10" data-testid={`faq-item-${i}`}>
                  <button
                    type="button"
                    onClick={() => setOpenIndex(openIndex === i ? -1 : i)}
                    data-testid={`faq-question-${i}`}
                    className="w-full flex items-center justify-between gap-6 py-6 text-left group"
                  >
                    <span className={`font-display text-lg sm:text-xl font-medium tracking-tight transition-colors duration-300 ${openIndex === i ? "text-brand" : "text-bone group-hover:text-brand"}`}>
                      {f.q}
                    </span>
                    <motion.span
                      animate={{ rotate: openIndex === i ? 45 : 0 }}
                      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                      className={`shrink-0 w-8 h-8 rounded-full border flex items-center justify-center transition-colors duration-300 ${openIndex === i ? "border-brand text-brand" : "border-white/20 text-slate-400"}`}
                    >
                      <Plus size={16} />
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false}>
                    {openIndex === i && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                      >
                        <p className="pb-6 text-slate-400 leading-relaxed max-w-2xl" data-testid={`faq-answer-${i}`}>{f.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-ink-soft border-t border-white/5" data-testid="faq-cta">
        <div className="section-shell text-center">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-bone max-w-2xl mx-auto leading-[1.1]">
              Ready when you are.
            </h2>
            <Link to="/apply" data-testid="faq-cta-apply" className="btn-primary group mt-10">
              Start Your Journey
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Faq;
